import { FC } from 'react';

import { useUser } from '@auth-v2';
import { useAtomValue } from 'jotai';

import { ParameterFilter, ParameterFilterProvider } from '@blocks';
import { LAYOUT_TYPE } from '@data';
import { useSaveSwingFoundationParameterSelection, useToggle } from '@hooks';
import { useTranslate } from '@i18n';
import { useParameterGroups } from '@queries';
import { layoutAtom } from '@store';
import { Box, Button, Screen, u } from '@ui';

import { displaySelectedParameterIds, withAtomsScope } from './atoms';
import { PositionGroup } from './PositionGroup';
import { SaveAsCopyConfirmationModal } from './SaveAsCopyConfirmationModal';

export type SwingFoundationsEditorProps = {
    onClose: () => void;
};

const SwingFoundationsEditorComponent: FC<SwingFoundationsEditorProps> = ({ onClose }) => {
    const activeLayout = useAtomValue(layoutAtom);
    const { data: user } = useUser();
    const selectedParameterIds = useAtomValue(displaySelectedParameterIds);
    const saveParameterSelection = useSaveSwingFoundationParameterSelection();
    const [isLayoutNameModalOpen, toggleIsLayoutNameModalOpen] = useToggle();

    const { data: parameterGroups } = useParameterGroups();

    const isPublicLayout = activeLayout?.layoutType === LAYOUT_TYPE.PUBLIC;
    const isOwnerUpdatingPublicLayout = isPublicLayout && activeLayout.ownerUserId === user?.id;
    const shouldUpdateCurrentLayout = !isPublicLayout || isOwnerUpdatingPublicLayout;

    const t = useTranslate();

    return (
        <>
            <Screen.Root
                title={t('swing_foundations_editor.title')}
                header={{
                    static: {
                        end: (
                            <>
                                <Button variant="secondary" onClick={onClose}>
                                    {t('generic_ui.cancel')}
                                </Button>
                                {!shouldUpdateCurrentLayout ? (
                                    <Button variant="primary" onClick={toggleIsLayoutNameModalOpen}>
                                        {t('generic_ui.save_as')}
                                    </Button>
                                ) : (
                                    <Button
                                        variant="primary"
                                        onClick={async () => {
                                            saveParameterSelection(selectedParameterIds);
                                            onClose();
                                        }}
                                    >
                                        {t('generic_ui.save')}
                                    </Button>
                                )}
                            </>
                        ),
                    },
                }}
            >
                <Screen.Column span="20/20">
                    <ParameterFilterProvider>
                        <Box paddingBlockEnd={u(2)}>
                            <ParameterFilter />
                        </Box>
                        {parameterGroups.map((group) => (
                            <PositionGroup key={group.id} {...group} />
                        ))}
                    </ParameterFilterProvider>
                </Screen.Column>
            </Screen.Root>
            {isLayoutNameModalOpen && (
                <SaveAsCopyConfirmationModal
                    onDismiss={toggleIsLayoutNameModalOpen}
                    onSubmit={async () => {
                        saveParameterSelection(selectedParameterIds);
                        onClose();
                    }}
                />
            )}
        </>
    );
};

export const SwingFoundationsEditor = withAtomsScope(SwingFoundationsEditorComponent);
